/**
 * チャットボット（Thoth）コンポーネント
 *
 * AIを使った曖昧検索のメインUI
 * - 機能A: 検索フィードバック（👍👎ボタン）
 * - 機能B: 音声読み上げ（Web Speech API）
 * - 機能C: データ蓄積（検索履歴・会話履歴）
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { getPages } from '../../services/pages';
import { detectIntent, generateConversation, fuzzySearch } from '../../services/gemini';
import { saveSearchFeedback, saveSearchHistory, saveConversation } from '../../services/feedback';
import { checkAndResetMonthlyCount } from '../../services/users';
import { doc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useSpeech } from '../../hooks/useSpeech';
import { useT, useLanguage } from '../../i18n';

function ChatBot() {
  const { user } = useAuth();
  const { userData, hasReachedQueryLimit, remainingQueries, isPremium } = useUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // 音声読み上げフック
  const { isSupported: speechSupported, speak, isSpeakingId } = useSpeech();

  // 多言語対応
  const { t } = useT();
  const { lang } = useLanguage();

  // 音声入力の状態
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // 音声認識APIの対応チェック
  const speechRecognitionSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // 音声認識完了後に自動送信するためのref
  const pendingVoiceInputRef = useRef(null);

  // 音声入力による検索かどうかを追跡（自動読み上げ用）
  const isVoiceSearchRef = useRef(false);

  // 音声認識の初期化
  useEffect(() => {
    if (!speechRecognitionSupported) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    // ブラウザの言語設定に従う
    const browserLang = navigator.language || 'ja-JP';
    recognition.lang = browserLang.startsWith('ja') ? 'ja-JP' : 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;  // 途中結果は不要

    // 音声認識結果のハンドラ
    recognition.onresult = (event) => {
      // 最終結果のみ取得（isFinalがtrueのもの）
      const result = event.results[event.results.length - 1];
      if (result.isFinal) {
        const transcript = result[0].transcript;
        // 重複を防ぐため、前の入力を上書き（追加ではなく置換）
        pendingVoiceInputRef.current = transcript;
        setInput(transcript);
      }
    };

    // エラーハンドラ
    recognition.onerror = (event) => {
      console.error('[KnoVelo] 音声認識エラー:', event.error);
      setIsListening(false);
      pendingVoiceInputRef.current = null;
    };

    // 終了ハンドラ - 音声認識が完了したら自動送信
    recognition.onend = () => {
      setIsListening(false);
      // 音声入力があれば自動送信
      if (pendingVoiceInputRef.current) {
        // 少し待ってから送信（UIの更新を待つ）
        setTimeout(() => {
          const voiceInput = pendingVoiceInputRef.current;
          if (voiceInput) {
            pendingVoiceInputRef.current = null;
            // 直接送信処理を呼び出す
            handleVoiceSend(voiceInput);
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [speechRecognitionSupported]);

  /**
   * 音声入力の開始/停止
   */
  function toggleVoiceInput() {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  }

  // 初期メッセージを表示
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'welcome',
        type: 'bot',
        content: t('thoth.welcome'),
        timestamp: new Date()
      }]);
    }
  }, [t]);

  // メッセージが追加されたらスクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * 音声入力からのメッセージ送信（自動送信用）
   */
  async function handleVoiceSend(voiceText) {
    if (!voiceText.trim() || isLoading) return;
    // inputを更新してからhandleSendの処理を実行
    setInput('');
    // 音声入力フラグを設定（自動読み上げ用）
    isVoiceSearchRef.current = true;
    await processMessage(voiceText.trim());
    isVoiceSearchRef.current = false;
  }

  /**
   * メッセージを送信
   */
  async function handleSend() {
    if (!input.trim() || isLoading) return;
    const text = input.trim();
    setInput('');
    await processMessage(text);
  }

  /**
   * メッセージを処理（共通処理）
   */
  async function processMessage(text) {
    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // 会話履歴を保存（ユーザーメッセージ）
    saveConversation(user.uid, 'user', userMessage.content);

    try {
      // ユーザーの意図を判定
      const intentResult = await detectIntent(userMessage.content);
      console.log('[KnoVelo] 意図判定結果:', intentResult);

      // エラーの場合
      if (intentResult.intent === 'error') {
        const botMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: intentResult.response,
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, botMessage]);
        // 会話履歴を保存（Thoth応答）
        saveConversation(user.uid, 'thoth', intentResult.response);
        setIsLoading(false);
        return;
      }

      // 問い合わせの場合
      if (intentResult.intent === 'contact') {
        const contactUrl = '/lp/contact.html';
        const contactMessage = lang === 'en'
          ? `For inquiries, feedback, or bug reports, please use our contact form:\n\nhttps://app.knovelo.com${contactUrl}\n\nWe'll respond as soon as possible.`
          : `お問い合わせ・ご意見・バグ報告は、以下のフォームからお願いいたします。\n\nhttps://app.knovelo.com${contactUrl}\n\nできる限り早くご返信いたします。`;
        const botMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: contactMessage,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        saveConversation(user.uid, 'thoth', contactMessage);
        setIsLoading(false);
        return;
      }

      // 挨拶または会話の場合
      if (intentResult.intent === 'greeting' || intentResult.intent === 'conversation') {
        // ページ数を取得（会話生成用）
        let pageCount = 0;
        try {
          const pages = await getPages(user.uid, { limitCount: 1 });
          pageCount = pages.length > 0 ? userData?.storageUsed || pages.length : 0;
        } catch {
          // ページ取得エラーは無視
        }

        // 意図判定の応答を使用、または会話を生成
        let response = intentResult.response;
        if (!response || response.trim() === '') {
          response = await generateConversation(userMessage.content, pageCount);
        }

        const botMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        // 会話履歴を保存（Thoth応答）
        saveConversation(user.uid, 'thoth', response);
        setIsLoading(false);
        return;
      }

      // 検索の場合：月次リセットをチェック
      await checkAndResetMonthlyCount(user.uid);

      // Firestoreから最新のユーザーデータを取得して制限チェック
      const latestUserDoc = await getDoc(doc(db, 'users', user.uid));
      const latestUserData = latestUserDoc.data();
      const currentPlan = latestUserData?.plan || 'free';
      const monthlyLimit = currentPlan === 'pro' || currentPlan === 'premium' ? 20 : 5;
      const currentCount = latestUserData?.monthlyQueryCount || 0;
      const isLimitReached = currentCount >= monthlyLimit;

      console.log('[KnoVelo Chat] 検索制限チェック:', { currentPlan, monthlyLimit, currentCount, isLimitReached });

      if (isLimitReached) {
        const isProUser = currentPlan === 'pro' || currentPlan === 'premium';
        const limitMessage = '今月のAI検索回数が上限に達しました。\n\n' +
          (isProUser
            ? '来月のリセットをお待ちください。'
            : 'Proプランにアップグレードすると、月20回まで検索できます。');
        const botMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: limitMessage,
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, botMessage]);
        saveConversation(user.uid, 'thoth', limitMessage);
        setIsLoading(false);
        return;
      }

      // ユーザーのページを取得
      const pages = await getPages(user.uid, { limitCount: 100 });

      // デバッグ: 取得したページ情報を出力
      console.log('[KnoVelo Chat] 取得したページ数:', pages.length);
      console.log('[KnoVelo Chat] ページ一覧:', pages.map(p => ({
        pageId: p.pageId,
        title: p.title,
        summary: p.summary
      })));

      // ページが0件の場合
      if (pages.length === 0) {
        const noPageMessage = 'まだページが保存されていないようです。Chrome拡張機能でページを保存すると、後から曖昧な記憶でも探せるようになりますよ！';
        const botMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: noPageMessage,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        saveConversation(user.uid, 'thoth', noPageMessage);
        setIsLoading(false);
        return;
      }

      // 検索クエリを使用（意図判定から取得、またはユーザー入力をそのまま使用）
      const searchQuery = intentResult.searchQuery || userMessage.content;
      console.log('[KnoVelo Chat] 検索クエリ:', searchQuery);

      // AI検索を実行
      console.log('[KnoVelo Chat] fuzzySearch呼び出し開始');
      const result = await fuzzySearch(searchQuery, pages);
      console.log('[KnoVelo Chat] fuzzySearch結果:', result);

      // 検索回数をインクリメント
      await updateDoc(doc(db, 'users', user.uid), {
        monthlyQueryCount: increment(1)
      });

      // 検索履歴を保存
      const resultCount = result.results ? result.results.length : 0;
      saveSearchHistory(user.uid, searchQuery, resultCount);

      // 検索結果が0件の場合（エラータイプに応じたメッセージを表示）
      if (!result.results || result.results.length === 0) {
        const botMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: result.message,  // fuzzySearchから返されたメッセージを使用
          timestamp: new Date(),
          isError: result.errorType === 'server_busy' || result.errorType === 'api_error'
        };
        setMessages(prev => [...prev, botMessage]);
        saveConversation(user.uid, 'thoth', result.message);
        setIsLoading(false);
        return;
      }

      // 結果をメッセージに追加
      const botMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content: result.message,
        timestamp: new Date(),
        results: result.results,
        searchQuery: searchQuery,  // フィードバック用に検索クエリを保存
        isSearchResult: true,  // 検索結果であることを示すフラグ
        autoSpeak: isVoiceSearchRef.current  // 音声入力の場合は自動読み上げ
      };
      setMessages(prev => [...prev, botMessage]);
      saveConversation(user.uid, 'thoth', result.message);

    } catch (error) {
      console.error('エラー:', error);
      const errorMessage = '一時的にエラーが発生しました。しばらくしてからもう一度お試しください。';
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        content: errorMessage,
        timestamp: new Date(),
        isError: false
      }]);
      saveConversation(user.uid, 'thoth', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Enterキーで送信
   */
  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* ヘッダー */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200">
        <img
          src="/knovelo-trans-icon.png"
          alt="Thoth"
          className="w-10 h-10 rounded-full"
        />
        <div>
          <h2 className="font-bold text-gray-900">{t('thoth.name')}</h2>
          <p className="text-xs text-gray-500">{t('thoth.tagline')}</p>
        </div>
        {/* 残り回数 */}
        <div className="ml-auto text-right">
          <p className="text-xs text-gray-400">{t('thoth.remainingSearch')}</p>
          <p className={`text-sm font-bold ${remainingQueries <= 3 ? 'text-orange-500' : 'text-gray-600'}`}>
            {remainingQueries}{lang === 'ja' ? '回' : ' ' + t('thoth.times')}
          </p>
        </div>
      </div>

      {/* メッセージエリア */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            userId={user.uid}
            speechSupported={speechSupported}
            speak={speak}
            isSpeakingId={isSpeakingId}
          />
        ))}

        {/* ローディング */}
        {isLoading && (
          <div className="flex items-start gap-3">
            <img
              src="/knovelo-trans-icon.png"
              alt="Thoth"
              className="w-8 h-8 rounded-full flex-shrink-0"
            />
            <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 入力エリア */}
      <div className="p-4 border-t border-gray-200">
        {/* クイックアクション */}
        <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
          {[
            t('thoth.suggestions.yesterday'),
            t('thoth.suggestions.lastWeekNews'),
            t('thoth.suggestions.techArticle'),
            t('thoth.suggestions.readLater')
          ].map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => setInput(suggestion)}
              className="flex-shrink-0 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>

        {/* 入力フォーム */}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('thoth.placeholder')}
            className="input-field resize-none h-12 py-3"
            rows={1}
            disabled={isLoading || isListening}
          />
          {/* マイクボタン（音声入力対応ブラウザのみ表示） */}
          {speechRecognitionSupported && (
            <button
              onClick={toggleVoiceInput}
              disabled={isLoading}
              className={`px-3 rounded-lg transition-all ${
                isListening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={isListening ? t('thoth.voiceInputStop') : t('thoth.voiceInputStart')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/>
              </svg>
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="btn-primary px-4 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>

        {/* AI免責文言 */}
        <p className="text-xs text-gray-400 text-center mt-2">
          {t('thoth.disclaimer')}
        </p>
      </div>
    </div>
  );
}

/**
 * チャットメッセージコンポーネント
 */
function ChatMessage({ message, userId, speechSupported, speak, isSpeakingId }) {
  const isUser = message.type === 'user';

  // フィードバック状態
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);

  // 自動読み上げ（音声入力による検索結果の場合）
  useEffect(() => {
    if (message.autoSpeak && speechSupported && !isUser) {
      // 少し遅延させてUIが更新されてから読み上げ
      const timer = setTimeout(() => {
        const speechText = getSpeechTextForAutoSpeak();
        speak(speechText, message.id);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [message.autoSpeak, message.id, speechSupported, isUser]);

  /**
   * 自動読み上げ用のテキストを生成
   */
  function getSpeechTextForAutoSpeak() {
    let text = message.content;
    if (message.results && message.results.length > 0) {
      text += '\n\n';
      message.results.forEach((result, index) => {
        const title = result.page?.title || 'タイトルなし';
        const summary = result.page?.summary || '';
        const reason = result.reason || '';
        text += `${index + 1}件目。${title}。`;
        if (reason) {
          text += `${reason}。`;
        }
        if (summary) {
          text += `要約：${summary}。`;
        }
        text += '\n';
      });
    }
    return text;
  }

  /**
   * フィードバックを送信
   */
  async function handleFeedback(rating) {
    if (feedbackGiven) return;

    try {
      await saveSearchFeedback(
        userId,
        message.searchQuery || '',
        message.content,
        rating
      );
      setFeedbackGiven(true);
      setFeedbackType(rating);
    } catch (error) {
      console.error('[KnoVelo] フィードバック送信エラー:', error);
    }
  }

  /**
   * 読み上げ用のテキストを生成（検索結果がある場合はタイトル・要約も含める）
   */
  function getSpeechText() {
    let text = message.content;

    // 検索結果がある場合、タイトルと要約を追加
    if (message.results && message.results.length > 0) {
      text += '\n\n';
      message.results.forEach((result, index) => {
        const title = result.page?.title || 'タイトルなし';
        const summary = result.page?.summary || '';
        const reason = result.reason || '';

        text += `${index + 1}件目。${title}。`;
        if (reason) {
          text += `${reason}。`;
        }
        if (summary) {
          text += `要約：${summary}。`;
        }
        text += '\n';
      });
    }

    return text;
  }

  /**
   * 音声読み上げを開始/停止
   */
  function handleSpeak() {
    const speechText = getSpeechText();
    speak(speechText, message.id);
  }

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* アバター */}
      {!isUser && (
        <img
          src="/knovelo-trans-icon.png"
          alt="Thoth"
          className="w-8 h-8 rounded-full flex-shrink-0"
        />
      )}

      {/* メッセージ */}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-primary text-white rounded-tr-none'
            : message.isError
              ? 'bg-red-50 text-red-600 rounded-tl-none'
              : 'bg-gray-100 text-gray-800 rounded-tl-none'
        }`}
      >
        {/* テキスト */}
        <p className="whitespace-pre-wrap text-sm">{message.content}</p>

        {/* 検索結果 */}
        {message.results && message.results.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.results.map((result, index) => (
              <Link
                key={result.pageId}
                to={`/pages/${result.pageId}`}
                className="block p-3 bg-white rounded-xl hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-2">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">
                      {result.page?.title || 'タイトルなし'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {result.reason}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* アクションボタン（Bot メッセージのみ） */}
        {!isUser && (
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-200/50">
            {/* 音声読み上げボタン */}
            {speechSupported && (
              <button
                onClick={handleSpeak}
                className={`p-1.5 rounded-lg transition-colors ${
                  isSpeakingId(message.id)
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200/50'
                }`}
                title={isSpeakingId(message.id) ? '停止' : '読み上げ'}
              >
                {isSpeakingId(message.id) ? (
                  // 停止アイコン
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                ) : (
                  // 読み上げアイコン
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
            )}

            {/* フィードバックボタン（検索結果がある場合のみ） */}
            {message.isSearchResult && (
              <>
                {feedbackGiven ? (
                  <span className="text-xs text-gray-400 ml-auto">
                    {feedbackType === 'good' ? '👍 ' : '👎 '}フィードバックありがとうございます
                  </span>
                ) : (
                  <div className="flex items-center gap-1 ml-auto">
                    <span className="text-xs text-gray-400 mr-1">役に立ちましたか？</span>
                    <button
                      onClick={() => handleFeedback('good')}
                      className="p-1.5 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                      title="役に立った"
                    >
                      👍
                    </button>
                    <button
                      onClick={() => handleFeedback('bad')}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="役に立たなかった"
                    >
                      👎
                    </button>
                  </div>
                )}
              </>
            )}

            {/* タイムスタンプ */}
            <span className={`text-xs ${message.isSearchResult && !feedbackGiven ? '' : 'ml-auto'} text-gray-400`}>
              {formatTime(message.timestamp)}
            </span>
          </div>
        )}

        {/* ユーザーメッセージのタイムスタンプ */}
        {isUser && (
          <p className="text-xs mt-2 text-white/70">
            {formatTime(message.timestamp)}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * 時刻をフォーマット
 */
function formatTime(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default ChatBot;
